import React, { useState } from 'react';
import './ConstructionTranslator.css';

// Import images so CRA (webpack) resolves them correctly
import imgSlab from '../../../assets/images/terminology/zone1-slab.png';
import imgRoof from '../../../assets/images/terminology/zone2-roof.png';
import imgInterior from '../../../assets/images/terminology/zone3-interior.png';
import imgStairs from '../../../assets/images/terminology/zone4-stairs.png';
import imgGarden from '../../../assets/images/terminology/zone5-garden.png';
import imgElectrical from '../../../assets/images/terminology/zone6-electrical.png';
import imgPlumbing from '../../../assets/images/terminology/zone7-plumbing.png';

// --- DATABASE: Terminology & Coordinates ---
const blueprintData = {
  slab: {
    title: "1. Land, Slab & Engineering",
    image: imgSlab,
    color: "#F5A623", 
    hotspots: [
    { id: '1a', x: 18, y: 45, title: "Datum Point", desc: "A fixed reference point (usually on the kerb) that determines the height of your floor levels." },
    { id: '1b', x: 35, y: 50, title: "Cut and Fill", desc: "Excavating into a slope (cut) and using that soil to level another area (fill). 'Fill' sites often require deeper piers." },
    { id: '1c', x: 58, y: 42, title: "Waffle Pod Slab", desc: "A concrete slab that sits on polystyrene pods. Cheaper and faster than a Raft Slab but requires careful site drainage." },
    { id: '1d', x: 81, y: 32, title: "Drop Edge Beam", desc: "A vertical step in the concrete slab, used when building on sloping land to retain soil." },
    { id: '1e', x: 55, y: 70, title: "Rebate", desc: "A step-down in the slab (usually 20mm) at wet areas (showers) or sliding doors to prevent water entering." }, /* NOTE: 1e is not visible in the image. Coordinates left as original. */
    { id: '1f', x: 53, y: 65, title: "Ag Line (Agricultural Pipe)", desc: "A slotted drainage pipe buried behind retaining walls to carry groundwater away." },
    { id: '1g', x: 54, y: 10, title: "Easement", desc: "A section of your land containing council assets (sewer/stormwater) that you generally cannot build over." },
    { id: '1h', x: 53, y: 85, title: "Boundary Trap", desc: "The final disconnection point between your house sewer and the main council sewer line." },
    { id: '1i', x: 81, y: 44, title: "Charged System", desc: "Stormwater pipes that remain full of water (under pressure) to push water up-hill to a street outlet." }
    ]
  },
roof: {
    title: "2. The Roof Structure",
    image: imgRoof,
    color: "#249b03",
    hotspots: [
      { id: '2a', x: 50, y: 25, title: "Truss", desc: "A pre-fabricated triangular timber frame that supports the roof." }, /* NOTE: 2a not visible in image. Coordinates kept as original. */
      { id: '2b', x: 48, y: 35, title: "Sarking", desc: "The reflective foil skin under tiles/metal. Essential for insulation and waterproofing (often skipped by cheap quotes)." },
      { id: '2c', x: 48, y: 48, title: "Battens", desc: "Small timber/metal strips fixed across trusses that the tiles or metal sheets screw into." },
      { id: '2d', x: 30, y: 71, title: "Fascia", desc: "The board running along the lower edge of the roof that holds the gutters." }, /* NOTE: There are multiple 2d labels; mapped to the leftmost one. */
      { id: '2e', x: 53, y: 76, title: "Soffit / Eave", desc: "The underside of the roof overhang. Needs ventilation to prevent mold in the roof cavity." },
      { id: '2f', x: 60, y: 50, title: "Valley", desc: "The internal angle where two roof slopes meet. High leak risk area – ensure 'Valley Irons' are installed." },
      { id: '2g', x: 49, y: 18, title: "Ridge Capping", desc: "The tiles or metal cap covering the very peak of the roof." },
      { id: '2h', x: 71, y: 23, title: "Flashing", desc: "Metal strips used to waterproof joints (e.g., around chimneys or skylights)." }
    ]
  },
interior: {
  title: "3. Interior & Kitchen",
  image: imgInterior,
  color: "#00d4ff",
  hotspots: [
    { id: '3a', x: 62, y: 19, title: "Bulkhead", desc: "A lowered section of ceiling, often used to define a kitchen area or hide plumbing/AC ducts." },
    { id: '3b', x: 91, y: 27, title: "Cornice", desc: "The moulding between wall and ceiling. Styles: Cove (Curved), Square Set (Modern), Shadow Line (Gap detail)." },
    { id: '3c', x: 39, y: 67, title: "Waterfall End", desc: "Stone benchtop material that continues vertically down the side of the cabinet to the floor." },
    { id: '3d', x: 57, y: 73, title: "Kickboard", desc: "The recessed plinth at the bottom of cabinets. Trend: Mirror kickboards make cabinets look like they are floating." }, /* NOTE: Mapped to the rightmost 3d label */
    { id: '3e', x: 51, y: 63, title: "Substrate", desc: "The material under your benchtop (usually MDF). Stone should never sit directly on open cabinetry." },
    { id: '3f', x: 33, y: 49, title: "Splashback", desc: "Material protecting the wall behind the cooktop (Glass, Tile, Stone)." },
    { id: '3g', x: 8, y: 27, title: "Rough-In", desc: "The phase where pipes and wires are run inside the walls before the plasterboard goes on." } /* NOTE: Mapped to the topmost 3g label */
  ]
},
  stairs: {
    title: "4. Staircase",
    image: imgStairs,
    color: "#d4af37",
    hotspots: [
      { id: '4a', x: 56, y: 73, title: "Stringer", desc: "The structural board running up the side of the stairs that supports the treads. (Closed or Mono stringers)." },
      { id: '4b', x: 68, y: 47, title: "Tread", desc: "The horizontal board you step on." },
      { id: '4c', x: 64, y: 53, title: "Riser", desc: "The vertical board between treads. 'Open Riser' means there is a gap (see-through)." },
      { id: '4d', x: 60, y: 59, title: "Nosing", desc: "The edge of the tread that protrudes over the riser." },
      { id: '4e', x: 67, y: 27, title: "Balustrade", desc: "The barrier/fence preventing falls (Glass, semi-frameless, wire, or timber)." }, /* NOTE: 4e is not visible in the image. Coordinates left as original. */
      { id: '4f', x: 36, y: 67, title: "Newel Post", desc: "The large structural post at the start/turn of the stairs." },
      { id: '4g', x: 45, y: 40, title: "Void", desc: "The open empty space looking down from the floor above." }
    ]
  },
garden: {
    title: "5. Garden & Fencing",
    image: imgGarden,
    color: "#e74c3c",
    hotspots: [
      { id: '5a', x: 25, y: 70, title: "Retaining Wall", desc: "A structure holding back soil. Over 600mm or 1m usually requires an Engineer." }, /* NOTE: 5a not visible in image. Coordinates kept as original. */
      { id: '5b', x: 47, y: 43, title: "Sleeper", desc: "Heavy timber or concrete beams used for retaining." },
      { id: '5c', x: 72, y: 49, title: "Plinth Board", desc: "The board at the bottom of a fence to stop soil moving between neighbours." },
      { id: '5d', x: 80, y: 26, title: "Capping Rail", desc: "The timber finishing piece on top of a fence." },
      { id: '5e', x: 57, y: 34, title: "Weep Hole", desc: "Small gaps in retaining walls (or house brickwork) to let trapped water escape." }, /* NOTE: Mapped to the upper 5e label */
      { id: '5f', x: 63, y: 65, title: "Hardscape", desc: "Paving, concrete, and brick structures in the garden." },
      { id: '5g', x: 31, y: 28, title: "Softscape", desc: "Plants, turf, and mulch in the landscaping." }
    ]
  },
 electrical: {
    title: "6. Electrical Systems",
    image: imgElectrical,
    color: "#F5A623",
    hotspots: [
      { id: '6a', x: 18, y: 64, title: "Switchboard / Distribution Board", desc: "The central hub for electrical routing. Must contain RCDs (safety switches) to cut power instantly if a fault is detected." },
      { id: '6b', x: 26, y: 44, title: "GPO (General Purpose Outlet)", desc: "The industry term for a power point." },
      { id: '6c', x: 67, y: 58, title: "10A vs 15A/20A Circuits", desc: "10A are standard outlets. 15A/20A are heavy-duty dedicated circuits with thicker wiring for ovens or induction cooktops." },
      { id: '6d', x: 86, y: 56, title: "Isolator Switch", desc: "A robust, hard-wired switch placed next to large appliances (A/C, Hot Water) allowing them to be safely disconnected." }, /* NOTE: Mapped to the rightmost 6d label near the A/C unit */
      { id: '6e', x: 40, y: 35, title: "Rough-In Cabling", desc: "Wires hidden in walls. Includes TPS cable for power, Cat6 for high-speed data, and Coaxial for antennas." }, /* NOTE: Mapped to the leftmost 6e label */
      { id: '6f', x: 68, y: 41, title: "Lighting Circuits", desc: "Downlights, Pendants, LED Strips controlled by standard, Dimmer, or 2-Way Switches (controlling one light from two locations)." },
      { id: '6g', x: 26, y: 70, title: "Smart Meter", desc: "A digital electricity meter that records usage in real-time and sends data remotely to the provider." }
    ]
  },
  plumbing: {
    title: "7. Plumbing & Drainage",
    image: imgPlumbing,
    color: "#00d4ff",
    hotspots: [
      { id: '7a', x: 7, y: 36, title: "Rough-In Pipes", desc: "The hidden network. Uses PEX (flexible plastic) or Copper for pressurized hot/cold water, and PVC for gravity-fed wastewater." }, /* NOTE: Mapped to upper 7a label */
      { id: '7b', x: 17, y: 55, title: "Cistern", desc: "The water storage tank for a toilet. Modern trends use In-Wall (Concealed) Cisterns hidden behind the gyprock." }, /* NOTE: Mapped to 7b label on the toilet */
      { id: '7c', x: 37, y: 40, title: "Shower Screen & Drainage", desc: "Screens (Frameless/Semi) and Smart/Tile-Insert Drains (linear grates holding a matching floor tile to become almost invisible)." },
      { id: '7d', x: 23, y: 35, title: "Tapware Mixers", desc: "A single lever blending hot/cold water, or Thermostatic Valves which maintain an exact water temperature." }, /* NOTE: Mapped to the left 7d label */
      { id: '7e', x: 69, y: 63, title: "Stormwater System", desc: "Manages rainwater. Includes gutters, downpipes, and underground pipes leading to a street outlet or an OSD (On-Site Detention) tank." }, /* NOTE: Mapped to the lower 7e label on the pipe */
      { id: '7f', x: 52, y: 86, title: "Sewerage & Boundary Trap", desc: "The gravity-fed system removing waste. The Boundary Trap is the final disconnection point before your private sewer connects to the public mains." }
    ]
  }
};

const ConstructionTranslator = () => {
  const [activeZone, setActiveZone] = useState('slab');
  const [hoveredSpot, setHoveredSpot] = useState(null);

  const currentData = blueprintData[activeZone];

  return (
    <div className="translator-wrapper">
      
      <div className="translator-header">
        <h2>CONSTRUCTION TERMINOLOGY</h2>
        <p>Select a zone below and hover over the pulsing dots to decode the jargon.</p>
      </div>

      <div className="translator-layout">
        
        {/* LHS: Zone Selection List */}
        <div className="translator-sidebar">
          {Object.entries(blueprintData).map(([key, zone]) => (
            <button 
              key={key}
              className={`sidebar-btn ${activeZone === key ? 'active' : ''}`}
              onClick={() => setActiveZone(key)}
            >
              <div className="btn-indicator" style={{ backgroundColor: activeZone === key ? zone.color : 'transparent' }}></div>
              <span>{zone.title}</span>
            </button>
          ))}
        </div>

        {/* RHS: Interactive Image Map */}
        <div className="translator-content">
          <div className="interactive-map-wrapper">
            {/* The Background Image updates based on the LHS selection */}
            <img 
              src={currentData.image} 
              alt={currentData.title} 
              className="blueprint-image"
              onError={(e) => { e.target.style.display = 'none'; }} // Hide if image fails to load
            />

            {/* Render Hotspots for the currently selected zone */}
            {currentData.hotspots.map((spot) => (
              <div 
                key={spot.id}
                className="hotspot-wrapper"
                style={{ top: `${spot.y}%`, left: `${spot.x}%` }}
                onMouseEnter={() => setHoveredSpot(spot.id)}
                onMouseLeave={() => setHoveredSpot(null)}
              >
                {/* The pulsing dot */}
                <div className="hotspot-dot" style={{ backgroundColor: currentData.color }}>
                  <div className="hotspot-pulse" style={{ borderColor: currentData.color }}></div>
                  <span className="hotspot-label">{spot.title}</span>
                </div>

                {/* The Popup Tooltip */}
                <div className={`hotspot-tooltip ${hoveredSpot === spot.id ? 'visible' : ''}`}>
                  <h4 style={{ color: currentData.color }}>{spot.title}</h4>
                  <p>{spot.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ConstructionTranslator;